import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef, MatSelectChange, MatSnackBar} from '@angular/material';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LocalAttraction } from '../models/local-attraction.model';
import { formatPhoneString, undoPhoneFormat } from '../../util/phone';
import { GoogleMap } from '@agm/core/services/google-maps-types';
import { AgmMap } from '@agm/core';
import { LatLon } from '../../util/geodesy/latlon';
import { GoogleService } from '../../google/google.service';
import { LocalAttractionService } from '../service/local-attraction.service';
import { CountriesService } from '../../shared/services/countries.service';
import { Country } from '../../business/models/country.model';
import { State } from '../../business/models/state.model';


@Component({
  selector: 'app-edit-local-attraction',
  templateUrl: './edit-local-attraction.component.html',
  styleUrls: ['./edit-local-attraction.component.scss']
})
export class EditLocalAttractionComponent implements OnInit, AfterViewInit {
  localAttraction: LocalAttraction;
  origLocalAttraction: LocalAttraction;
  isNew = false;
  isLoading = true;
  airportID: number;
  itemsToChange: Set<number>;
  formGroup: FormGroup;
  selectedName: string;
  // selectedAddress: string;
  selectedStreet1: string;
  selectedCity: string;
  selectedState: string;
  selectedZip: string;
  selectedCountry: string;
  selectedTravelType: string;
  selectedPhone: string;
  selectedWebsite: string;
  latlon: LatLon;
  bounds:  google.maps.LatLngBoundsLiteral;
  countries: Country[];
  postalCodePlaceHolder = '';
  states: State [] = new Array<State>();

  @ViewChild('popupMap') popupMap: AgmMap;

  private _nativeLocalAttractionMap: GoogleMap;

  constructor(private _matDialogRef: MatDialogRef<EditLocalAttractionComponent>,
              public _snackBar: MatSnackBar,
              @Inject(MAT_DIALOG_DATA) data,
              private _google: GoogleService,
              private _cdref: ChangeDetectorRef,
              private _localAttractionService: LocalAttractionService,
              private countriesService: CountriesService) {
    this.localAttraction = data.localAttraction || new LocalAttraction();
    this.isNew = data.isNew || false;
    this.airportID = data.airID;
    this.origLocalAttraction = Object.assign(new LocalAttraction(), data.localAttraction);
    this.itemsToChange = new Set<number> ();
    this.selectedName = this.localAttraction.name;
    // this.selectedAddress = this.localAttraction.address;
    this.selectedStreet1 = this.localAttraction.street1;
    this.selectedCity = this.localAttraction.city;
    this.selectedState = this.localAttraction.state;
    this.selectedZip = this.localAttraction.zip;
    if (this.localAttraction.country && this.localAttraction.country.length > 0) {
      this.selectedCountry = this.localAttraction.country;
    } else {
      this.selectedCountry = 'US';
    }
    if (this.isNew) {
      this.selectedTravelType = '';
      this.latlon = this.localAttraction.location;
    } else {
      this.selectedTravelType = this.getTravelType(this.localAttraction);
    }
    if (this.localAttraction.location && this.localAttraction.location.lat && this.localAttraction.location.lon) {
      this.latlon = this.localAttraction.location;
    } else {
      this.latlon = data.airportLocation;
    }
    if (this.localAttraction.phone) {
      this.selectedPhone = formatPhoneString(this.localAttraction.phone);
    } else {
      this.selectedPhone = '';
    }
    this.selectedWebsite = this.localAttraction.website;
  }

  ngOnInit() {
    this._matDialogRef.backdropClick().subscribe(() => {
      this.close({cancelBtnClicked: true});
    });
    this.formGroup = new FormGroup({
      name: new FormControl(this.selectedName, [Validators.required, Validators.maxLength(256)]),
      street1: new FormControl(this.selectedStreet1, [Validators.required, Validators.maxLength(100)]),
      city: new FormControl(this.selectedCity, [Validators.required, Validators.maxLength(50)]),
      state: new FormControl(this.selectedState),
      zip: new FormControl(this.selectedZip, [Validators.required, Validators.maxLength(20)]),
      country: new FormControl(this.selectedCountry, [Validators.required, Validators.maxLength(30)]),
      type: new FormControl(this.selectedTravelType, [Validators.required]),
      phone: new FormControl(formatPhoneString(this.selectedPhone), [Validators.pattern('^([(]\\d{3}[)]\\s\\d{3}[-]\\d{4})$')]),
      website: new FormControl(this.selectedWebsite, [Validators.maxLength(256),
        Validators.pattern(
'^(http:\\/\\/www\\.|https:\\/\\/www\\.|http:\\/\\/|https:\\/\\/)?[a-z0-9]+([\\-\\.]{1}[a-z0-9]+)*\\.[a-z]{2,5}(:[0-9]{1,5})?(\\/.*)?$'
        )])
    });
    this.isLoading = false;
    // this.formGroup.patchValue({country: this.selectedCountry || ''});
    // this.formGroup.patchValue({state: this.selectedState || ''});
    this._setPostalCodeValidation(this.selectedCountry);
  }

  async ngAfterViewInit() {
    // const nativeMap = await this._initMap();
    // nativeMap.setCenter({ lat: this.latlon.lat, lng: this.latlon.lon });
    const nativeLocalAttractionMap = await this._initMap();
    nativeLocalAttractionMap.setCenter({ lat: this.latlon.lat, lng: this.latlon.lon });
    try {
      this.countries = await this.countriesService.getCountries();
    } catch (e) {
      this.close({cancelBtnClicked: false, success: false});
    }

    if (this.selectedCountry) {
      try {
        this.states = await this.countriesService.getStates(this.selectedCountry);
        this.states.length ? this.formGroup.controls['state'].setValidators([Validators.required, Validators.maxLength(40)]) :
          this.formGroup.controls['state'].setValidators([Validators.maxLength(30)]);
      } catch (e) {
        this.close({cancelBtnClicked: false, success: false});
      }
    }
  }

  private async _initMap(): Promise<GoogleMap> {
    return new Promise<GoogleMap>((resolve, reject) => {
      this.popupMap.mapReady.subscribe(
        map => {
          this._nativeLocalAttractionMap = map;
          resolve(map);
        },
        err => reject(err)
      );
    });
  }

  getTravelType(attraction: LocalAttraction): string {
    switch (attraction.travelType) {
      case 'Restaurant Nearby':
        return 'Restaurant Nearby';
      case 'Restaurant On Field':
        return 'Restaurant On Field';
      case 'Accommodations':
        return 'Accommodations';
      case 'Transportation':
        return 'Transportation';
      case 'Attractions':
        switch (attraction.travelRef) {
          case 'Museum':
            return 'Museum';
          case 'Golf':
            return 'Golf';
          case 'National Park':
            return 'National Park';
          case 'State or Local Park':
            return 'State or Local Park';
        }
    }
    return 'Attractions';
  }

  hasAddress(): boolean {
    if (this.selectedCountry && this.selectedCity && this.selectedStreet1) {
      return (this.selectedCountry.length > 0 && this.selectedCity.length > 0 && this.selectedStreet1.length > 0);
    } else {
      return false;
    }
  }
  /**
   * triggered when value inside countries dropdown changes; event.value contains two digit country code.
   * @param {MatSelectChange} event
   * @return {Promise<void>}
   */
  async countryChange(event: MatSelectChange) {
    // if value property is empty return
    if (!event.value) { return; }
    if (event.source.id.toString() === '1') {
      await this._setAddressForm(event.value);
      if (event.value === this.selectedCountry) {
        this.formGroup.patchValue({state: this.selectedState || ''});
      }
    }
  }

  /**
   * changes states dropdown when a different country is chosen.
   * also changes validators depending on whether or states list exists for country
   * @param {string} value
   * @return {Promise<void>}
   * @private
   */
  async _setAddressForm(value: string) {
    // this.isLoading = true;
    try {
      this.states = await this.countriesService.getStates(value);
    } catch (e) {
      this.close({cancelBtnClicked: false, success: false});
    }
    this.formGroup.patchValue({state: ''});
    // this.isLoading = false;
    if (!this.states.length) {
      this.formGroup.controls['state'].setValidators([]);
    } else {
      this.formGroup.controls['state'].setValidators([Validators.required]);
    }
    this._setPostalCodeValidation(value);
  }

  /**
   * If countryCode is 'US', Postal Code field becomes required.
   * @param {string} countryCode
   * @private
   */
  _setPostalCodeValidation(countryCode: string) {
    if (countryCode === 'US') {
      this.postalCodePlaceHolder = 'Zip Code *';
      this.formGroup.controls['zip'].setValidators([
        Validators.required,
        Validators.pattern('^\\d{5}$')
      ]);
      this.formGroup.controls['zip'].updateValueAndValidity();
    } else {
      this.postalCodePlaceHolder = 'Postal Code';
      this.formGroup.controls['zip'].setValidators([Validators.maxLength(9)]);
      this.formGroup.controls['zip'].updateValueAndValidity();
    }
  }

  /**
   * formats passed in phone number string;
   * @param {string} field
   * @param {string} value
   * @return {string}
   */
  formatPhoneNumber (field: string, value: string): string {
    const NEW_NUMBER: string = formatPhoneString(value);
    this.formGroup.patchValue({[field]: NEW_NUMBER});
    return NEW_NUMBER;
  }

  /**
   * event triggered by keyup event on phone number or fax fields
   * @param event
   * @param {string} field
   * @param phoneInputObj
   */
  phoneFieldKeyUp (event, field: string, phoneInputObj) {
    const value: string = this.formGroup.get(field).value;
    const rawValue = undoPhoneFormat(value);
    const KEY = event.key;

    if (KEY === 'Backspace' && rawValue.length === 10 && phoneInputObj.selectionStart === rawValue.length) {
      this.formatPhoneNumber(field, value);
    } else if (phoneInputObj.selectionStart < value.length) {
    } else if (KEY === 'Backspace' || KEY === 'ArrowLeft' || KEY === 'ArrowRight') {
      return;
    } else {
      this.formatPhoneNumber(field, value);
    }
  }

  /**
   * triggered when phone number or fax field goes out of focus
   * @param {string} field
   */
  phoneFieldBlur(field: string) {
    const VALUE: string = this.formGroup.get(field).value;
    if (VALUE === '') {
      return;
    }
    this.formatPhoneNumber(field, VALUE);
  }

  private async setMarkerToAddress() {
    await this._google.promise;
    const address = this.selectedStreet1 + ', ' + this.selectedCity + ', ' + this.selectedCountry;
    const gc = await this._google.geocode({ address : address});
    if (gc[0]) {
      this._cdref.detectChanges();
      const geom = gc[0].geometry;
      switch (geom.location_type) {
        case google.maps.GeocoderLocationType.ROOFTOP:
        case google.maps.GeocoderLocationType.RANGE_INTERPOLATED:
          this.latlon = new LatLon(geom.location.lat(), geom.location.lng());
          this._nativeLocalAttractionMap.setCenter({ lat: this.latlon.lat, lng: this.latlon.lon });
          break;
        case google.maps.GeocoderLocationType.GEOMETRIC_CENTER:
        default:
          // When only the geometric center or an approximate location is available, Google returns a location instead
          // of bounds. However, since the location is approximate, instead of showing a marker,
          // we center the map on the location and zoom out a bit.
          this.latlon = new LatLon(geom.location.lat(), geom.location.lng());
          this._nativeLocalAttractionMap.setCenter({ lat: this.latlon.lat, lng: this.latlon.lon });
          // Add an offset around the center to force the map to zoom out a bit
          const offset = 0.08;
          const bounds = new google.maps.LatLngBounds();
          bounds.extend(new google.maps.LatLng(this.latlon.lat + offset, this.latlon.lon + offset));
          bounds.extend(new google.maps.LatLng(this.latlon.lat - offset, this.latlon.lon - offset));
          this._nativeLocalAttractionMap.fitBounds(bounds as any);
          break;
        /* default:
          if (geom.bounds) {
            // When only approximate location is available, use bounds instead of lat/lon
            this.bounds = geom.bounds.toJSON();
            this._nativeMap.fitBounds(this.bounds);
          } */
      }
    }
  }

  async pinDropped(event: any) {
    if (this.latlon && this.latlon.lat && this.latlon.lon) {
      Object.assign(this.latlon, new LatLon(event.coords.lat, event.coords.lng));
    } else {
      this.latlon = new LatLon (event.coords.lat, event.coords.lng);
    }
  }

  setTravelType(attractionType: string): string {
    switch (attractionType) {
      case 'Restaurant Nearby':
        return 'Restaurant Nearby';
      case 'Restaurant On Field':
        return 'Restaurant On Field';
      case 'Accommodations':
        return 'Accommodations';
      case 'Transportation':
        return 'Transportation';
      case 'Museum':
      case 'Golf':
      case 'National Park':
      case 'State or Local Park':
        return 'Attractions';
    }
    return 'Attractions';
  }

  setTravelRef(attractionType: string): string {
    switch (attractionType) {
      case 'Museum':
        return 'Museum';
      case 'Golf':
        return 'Golf';
      case 'National Park':
        return 'National Park';
      case 'State or Local Park':
        return 'State or Local Park';
    }
    return null;
  }

  /**
   * triggered by save button click event
   * @return {Promise<void>}
   */
  async save() {
    try {
      let res: any;
      this.localAttraction.travelType = this.setTravelType(this.formGroup.value.type);
      this.localAttraction.travelRef = this.setTravelRef(this.formGroup.value.type);
      this.localAttraction.phone = undoPhoneFormat(this.formGroup.value.phone);
      this.localAttraction.website = this.formGroup.value.website;
      this.localAttraction.name = this.formGroup.value.name;
      this.localAttraction.address = '';
      this.localAttraction.street1 = this.formGroup.value.street1;
      this.localAttraction.city = this.formGroup.value.city;
      this.localAttraction.state = this.formGroup.value.state;
      this.localAttraction.zip = this.formGroup.value.zip;
      this.localAttraction.country = this.formGroup.value.country;
      this.localAttraction.location = this.latlon;
      if (this.isNew) {
        res = await this._localAttractionService.postLocalAttraction(this.airportID, this.localAttraction);
        this.localAttraction = LocalAttraction.parse(res);
      } else {
        res = await this._localAttractionService.putLocalAttraction(this.airportID, this.localAttraction);
        Object.assign(this.localAttraction, LocalAttraction.parse(res));
        this.localAttraction = LocalAttraction.parse(res);
      }
      this.close({cancelBtnClicked: false, success: true, localAttraction: this.localAttraction});
    } catch (e) {
      this.close({cancelBtnClicked: false, success: false});
    }
  }

  /**
   * triggered by cancel button click event
   */
  cancel () {
    this.close({cancelBtnClicked: true});
  }

  /**
   * closes modal
   * @param data
   */
  close(data: any) {
    if (data && data.cancelBtnClicked) {
      Object.assign(this.localAttraction, this.origLocalAttraction);
    }
    data['isNew'] = this.isNew;
    this._matDialogRef.close(data);
  }
}
